import { IconPolygon } from "@assets/icons";
import { BrandLogo } from "@components/data-display";
import {
  CompensationItem,
  CSCompensationDialogOption,
  EVENT_CLOSE_CS_COMPENSATION_DIALOG,
  EVENT_OPEN_CS_COMPENSATION_DIALOG,
} from "@components/features/CSCompensation/CSCompensationDialog";
import ClaimButton, { ClaimButtonRef } from "@components/inputs/ClaimButton";
import useCSCompensation from "@hooks/useCSCompensation";
import { getThemeAsset } from "@utils/theme";
import { AnimatePresence, motion } from "framer-motion";
import { ButtonIcon, cn, Dialog, PageTitle, useCustomEventsListener } from "kz-ui-sdk";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdChevronLeft, MdChevronRight } from "react-icons/md";

// Title animation variants (small horizontal slide + fade)
const TITLE_SLIDE_VARIANTS = {
  enter: (dir: 1 | -1) => ({
    x: dir > 0 ? 40 : -40,
    opacity: 0,
    position: "relative" as const,
  }),
  center: {
    x: 0,
    opacity: 1,
    position: "relative" as const,
  },
  exit: (dir: 1 | -1) => ({
    x: dir > 0 ? -40 : 40,
    opacity: 0,
    position: "relative" as const,
  }),
} as const;

// Content slide variants (main staff+chat image)
const CONTENT_SLIDE_VARIANTS = {
  enter: (dir: 1 | -1) => ({
    x: dir > 0 ? 40 : -40,
    opacity: 0,
    position: "relative" as const,
    top: 0,
    left: 0,
    right: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
    position: "relative" as const,
  },
  exit: (dir: 1 | -1) => ({
    x: dir > 0 ? -40 : 40,
    opacity: 0,
    position: "relative" as const,
    top: 0,
    left: 0,
    right: 0,
  }),
} as const;

const MAX_DOTS_TO_SHOW = 10;

const MAX_CLAIM_DELAY = 300;

export default function CSCompensationDialog() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const refOnClaim = useRef<CSCompensationDialogOption["onClaim"]>();
  const refClaimBtn = useRef<ClaimButtonRef>(null);

  const [index, setIndex] = useState(0);
  const [isClaiming, setIsClaiming] = useState(false);
  const [direction, setDirection] = useState<1 | -1>(1);
  // Transient state to show "Claimed" after a successful claim
  const [justClaimed, setJustClaimed] = useState(false);
  const claimedTimerRef = useRef<number | null>(null);

  // Use hook as source of truth
  const { csCompensationItems = [], refetchCSCompensation } = useCSCompensation();

  // Sync derived items and close logic when open
  useEffect(() => {
    if (!open) return;

    const lastIndex = Math.max(csCompensationItems.length - 1, 0);

    // Auto-close when there is nothing left to show (skip while showing post-claim state)
    if (csCompensationItems.length === 0) {
      setOpen(false);
      setIsClaiming(false);
      return;
    }

    // Clamp index only when out of range (e.g., after removal of the last slide)
    if (index > lastIndex) {
      setDirection(-1);
    }
  }, [open, csCompensationItems.length, index]);

  useCustomEventsListener({
    [EVENT_OPEN_CS_COMPENSATION_DIALOG]: (event: CustomEvent<CSCompensationDialogOption>) => {
      handleOpen(event.detail || {});
    },
    [EVENT_CLOSE_CS_COMPENSATION_DIALOG]: () => {
      handleClose();
    },
  });

  const handleOpen = useCallback(({ onClaim }: CSCompensationDialogOption) => {
    refOnClaim.current = onClaim;
    setIndex(0);
    setIsClaiming(false);
    // Reset transient claimed state and timers
    if (claimedTimerRef.current) {
      window.clearTimeout(claimedTimerRef.current);
      claimedTimerRef.current = null;
    }
    setJustClaimed(false);
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
    setIsClaiming(false);
    if (claimedTimerRef.current) {
      window.clearTimeout(claimedTimerRef.current);
      claimedTimerRef.current = null;
    }
    setJustClaimed(false);
  }, []);

  const hasMultiple = csCompensationItems.length > 1;
  const prevCurrentRef = useRef<CompensationItem | null>(null);
  const current = useMemo(() => {
    const nextCurrent = csCompensationItems?.[index];
    if (nextCurrent) {
      prevCurrentRef.current = nextCurrent;
      return nextCurrent;
    }
    return prevCurrentRef.current;
  }, [csCompensationItems, index]);

  const goPrev = useCallback(() => {
    setDirection(-1);
    setIndex((prev) => Math.max(prev - 1, 0));
  }, []);
  const goNext = useCallback(() => {
    setDirection(1);
    setIndex((prev) => Math.min(prev + 1, Math.max(csCompensationItems.length - 1, 0)));
  }, [csCompensationItems.length]);

  const jumpToIndex = useCallback(
    (targetIndex: number) => {
      if (targetIndex === index) return;
      const lastIndex = Math.max(csCompensationItems.length - 1, 0);
      const clamped = Math.max(0, Math.min(targetIndex, lastIndex));
      setDirection(clamped > index ? 1 : -1);
      setIndex(clamped);
    },
    [index, csCompensationItems.length],
  );

  const isFirst = index === 0;
  const isLast = index === Math.max(csCompensationItems.length - 1, 0);

  const claimText = useMemo(() => `${t("Claim")} ${current?.amount}`, [t, current?.amount]);

  const handleClaim = useCallback(async () => {
    if (!current || isClaiming || justClaimed) return;
    setIsClaiming(true);

    try {
      await refOnClaim.current?.(current);
      refClaimBtn.current?.renderFlyingCoins();

      // Show "Claimed" briefly
      setJustClaimed(true);
      if (claimedTimerRef.current) {
        window.clearTimeout(claimedTimerRef.current);
      }
      claimedTimerRef.current = window.setTimeout(async () => {
        await refetchCSCompensation();
        setJustClaimed(false);
      }, MAX_CLAIM_DELAY);

      if (!isLast) {
        setDirection(1);
      }
    } catch {
      refetchCSCompensation();
    } finally {
      setIsClaiming(false);
    }
  }, [current, isClaiming, isLast, justClaimed, refetchCSCompensation]);

  useEffect(() => {
    if (index > csCompensationItems.length - 1) {
      setIndex(csCompensationItems.length - 1);
    }
  }, [csCompensationItems?.length, index]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (claimedTimerRef.current) {
        window.clearTimeout(claimedTimerRef.current);
        claimedTimerRef.current = null;
      }
    };
  }, []);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title={
        <div className="overflow-hidden">
          <AnimatePresence
            initial={false}
            mode="wait"
            custom={direction}
          >
            {current ? (
              <motion.div
                key={current?.key}
                custom={direction}
                variants={TITLE_SLIDE_VARIANTS}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.25, ease: "easeInOut" }}
              >
                <PageTitle
                  label={current?.title || ""}
                  variant="gradient"
                  classes={{
                    "label&": "w-full text-center line-clamp-2 !relative",
                    "labelOverlay&": "w-full text-center line-clamp-2",
                    "labelContainer&": "h-auto w-[calc(100%-16px)] mx-auto text-center",
                  }}
                />
              </motion.div>
            ) : null}
          </AnimatePresence>
        </div>
      }
      classes={{
        "container&":
          "relative z-[50] max-h-[calc(100dvh-32px)] flex flex-col !bg-grdLow pt-6 pb-5 px-0 rounded-[24px] border border-stroke-base shadow-none",
          "containerWrapper&": "!z-[9999]",
          "backDrop&": "!z-[9998]",
      }}
      transition
    >
      {/* Background */}
      <img
        src={"/images/cs-compensation/lights.png"}
        className="-z-1 pointer-events-none absolute left-0 top-0 h-auto w-full"
      />

      {/* Slider controls */}
      {hasMultiple && (
        <>
          <ButtonIcon
            onClick={goPrev}
            disabled={isFirst}
            aria-label={t("Previous compensation")}
            className={cn(
              "absolute -left-2 top-1/2 z-[5] size-11 -translate-y-1/2 rounded-[6px] bg-darkBrand-700 hover:bg-darkBrand-600",
              {
                "pointer-events-none !opacity-0": isFirst,
              },
            )}
            icon={<MdChevronLeft className="mx-auto my-auto size-6" />}
            size="lg"
          />
          <ButtonIcon
            onClick={goNext}
            disabled={isLast}
            aria-label={t("Next compensation")}
            className={cn(
              "absolute -right-2 top-1/2 z-[5] size-12 -translate-y-1/2 rounded-[6px] bg-darkBrand-700 hover:bg-darkBrand-600",
              {
                "pointer-events-none !opacity-0": isLast,
              },
            )}
            icon={<MdChevronRight className="mx-auto my-auto size-6" />}
            size="lg"
          />
        </>
      )}

      {/* Staff + Slide content */}
      <div className="relative mx-auto h-auto w-full max-w-[300px]">
        <AnimatePresence
          custom={direction}
          initial={false}
          mode="wait"
        >
          {current && (
            <motion.div
              key={current?.key}
              custom={direction}
              variants={CONTENT_SLIDE_VARIANTS}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.25, ease: "easeInOut" }}
            >
              <img
                src={getThemeAsset("/cs-compensation/staff.png")}
                className="-mt-[10px] aspect-[75/107] h-auto w-full"
              />
              {/* Staff Shirt Logo */}
              <BrandLogo
                size="auto"
                className="absolute left-[26.6%] top-[40%] !h-auto !w-[8%] max-w-[24px]"
              />
              <BrandLogo
                size="auto"
                className="absolute right-[12%] top-[39%] !h-auto !w-[8%] max-w-[24px]"
              />

              {/* Left Staff Chat Bubble*/}
              {current?.message && (
                <div className="absolute left-[22.67%] top-[54.5%] max-h-[118px] w-[52%] -translate-x-1/2 rounded-[16px] border border-accentBrand-700 bg-accentBrand-950 p-2 text-center text-sm shadow-md">
                  <div className="line-clamp-5 h-full max-h-full w-full">{current?.message}</div>
                  <div className="absolute -top-2 left-1/2 z-10 -translate-x-1/2 text-accentBrand-700">
                    <IconPolygon />
                  </div>
                </div>
              )}

              {/* Right Staff Chat Bubble*/}
              <div className="absolute right-[22.67%] top-[54.5%] max-h-[118px] w-[52%] translate-x-1/2 rounded-[16px] border border-accentBrand-700 bg-accentBrand-950 p-2 text-center text-sm shadow-md">
                <div className="line-clamp-5 h-full max-h-full w-full">
                  {t("claim your")} <br />
                  <div className="flex items-center justify-center gap-2 font-bold">
                    <img
                      src="/images/icon-coin.png"
                      className="h-4 w-4"
                    />{" "}
                    <p>{current?.amount ?? "0.00"}</p>
                  </div>
                  {t("gift")}
                </div>
                <div className="absolute -top-2 left-1/2 z-10 -translate-x-1/2 text-accentBrand-700">
                  <IconPolygon />
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Dots / Pager */}
      {hasMultiple && (
        <div className="absolute -bottom-6 left-1/2 z-10 -translate-x-1/2">
          {csCompensationItems.length > MAX_DOTS_TO_SHOW ? (
            <div className="flex translate-y-3 items-center justify-center rounded-full bg-darkBrand-700/40 px-3 py-1">
              <span className="text-xs text-white">
                {index + 1} / {csCompensationItems.length}
              </span>
            </div>
          ) : (
            <div className="flex max-w-full items-center justify-center overflow-x-auto px-2">
              <div className="flex shrink-0 items-center gap-4">
                {csCompensationItems.map((_, i) => (
                  <button
                    key={`dot-${i}`}
                    onClick={() => jumpToIndex(i)}
                    aria-label={`Go to item ${i + 1}`}
                    className={cn("size-3 rounded-full bg-white transition-colors", {
                      "!bg-accentBrand-500": i === index,
                    })}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Bottom Actions */}
      <div className="absolute bottom-0 w-full rounded-b-[24px] bg-grdMid p-4">
        <ClaimButton
          onClick={handleClaim}
          withCoin={true}
          withShine={true}
          claimable={!isClaiming && !justClaimed}
          text={justClaimed ? t("claimed") : claimText}
          loading={isClaiming}
          classes={{
            text: "text-base ",
          }}
          withFlyingCoins
          aria-label={"Claim"}
          size="lg"
          wrapperClassName="h-[56px]  [&_button]:h-[56px]"
          shineClassName="h-[56px]"
          ref={refClaimBtn}
        />
      </div>
    </Dialog>
  );
}
